import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import SelectDropdown, { SelectDropdownOption } from '../SelectDropdown';
import { RadioInput, RadioOption } from './DynamicQuestionForm.styled';
import { RejectionData } from '../../../types/api';
import { useHotelSources } from '../../../hooks';

// Styled wrapper components kept local
const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background : var(--color-white);
  padding: 1rem;
  border-radius: 0.375rem;
  // border: 1px solid var(--color-gray-200);
`;

const DropdownWrapper = styled.div<{ $highlight?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
  background-color: ${props => props.$highlight ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  border: 2px solid ${props => props.$highlight ? 'rgba(59, 130, 246, 0.3)' : 'transparent'};
  
  ${props => props.$highlight && `
    animation: pulse 0.6s ease-in-out;
  `}
  
  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
    }
  }
`;

const DropdownLabel = styled.label`
  font-size: 0.95rem;
  color: #374151;
  font-weight: 500;
`;

interface ApproveSourceSectionProps {
  question: any;
  rejectionReasons?: RejectionData[];
  onReviewChange?: (questionId: string, isApproved: boolean | null, rejectionReasonId?: string | null) => void;
}

export default function ApproveSourceSection({ 
  question, 
  rejectionReasons = [],
  onReviewChange
}: ApproveSourceSectionProps) {
  const [selected, setSelected] = useState<string>('');
  const [rejectReason, setRejectReason] = useState<string>('');
  const [highlightDropdown, setHighlightDropdown] = useState<boolean>(false);
  const { combinedReviews } = useHotelSources();
  // Get review data from combined reviews (main + draft) using question ID
  const contextReview = combinedReviews[question.id];

  // Initialize state from backend review data
  useEffect(() => {
    // Initialize based on context review whenever it changes or question changes
    if (contextReview) {
      if (contextReview.approved === true) {
        console.log('Setting to approved for question:', question.id);
        setSelected('approve');
        setRejectReason('');
      } else if (contextReview.approved === false) {
        setSelected('reject');
        setRejectReason(contextReview.reasonId || '');
      } else {
        setSelected('');
        setRejectReason('');
      }
    } else {
      // No review exists for this question
      setSelected('');
      setRejectReason('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextReview?.approved, contextReview?.reasonId, question.id]); // Reinitialize when review changes or question changes

  // Convert rejection reasons to dropdown options
  const rejectOptions: SelectDropdownOption[] = rejectionReasons.map((reason) => ({
    label: reason.reason_text,
    value: reason.id,
    sortOrder: 'asc',
  }));

  // Notify parent of review changes - only when user makes changes
  useEffect(() => {
    // Skip notifying if no selection has been made
    if (selected === '' && !rejectReason) return;
    
    if (onReviewChange) {
      if (selected === 'approve') {
        onReviewChange(question.id, true, null);
      } else if (selected === 'reject' && rejectReason) {
        onReviewChange(question.id, false, rejectReason);
      } else if (selected === '') {
        // Deselected - set to null
        onReviewChange(question.id, null, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, rejectReason, question.id]); // Removed onReviewChange to prevent infinite loop

  const handleApproveChange = () => {
    const newValue = selected === 'approve' ? '' : 'approve';
    setSelected(newValue);
    if (newValue === 'approve') {
      setRejectReason(''); // Clear rejection reason when approving
      setHighlightDropdown(false); // Clear highlight when approving
    }
  };

  const handleRejectChange = () => {
    const newValue = selected === 'reject' ? '' : 'reject';
    setSelected(newValue);
    
    // When selecting reject for the first time
    if (newValue === 'reject') {
      // Auto-select the first rejection reason if available
      if (rejectOptions.length > 0 && !rejectReason) {
        setRejectReason(rejectOptions[0].value);
      }
      
      // Trigger highlight animation
      setHighlightDropdown(true);
      
      // Remove highlight after animation completes
      setTimeout(() => {
        setHighlightDropdown(false);
      }, 1000);
    } else {
      // When deselecting reject
      setRejectReason(''); // Clear rejection reason when deselecting
      setHighlightDropdown(false);
    }
  };

  return (
    <SectionWrapper className='shadow-sm'>
      <div className="flex items-center gap-4">
        <RadioOption>
          <RadioInput
            type="radio"
            name={`approve_disapprove_${question.id}`}
            value="approve"
            checked={selected === 'approve'}
            onChange={handleApproveChange}
            aria-label={`Approve ${question.id}`}
          />
          <span>Approve</span>
        </RadioOption>

        <RadioOption>
          <RadioInput
            type="radio"
            name={`approve_disapprove_${question.id}`}
            value="reject"
            checked={selected === 'reject'}
            onChange={handleRejectChange}
            aria-label={`Reject ${question.id}`}
          />
          <span>Reject</span>
        </RadioOption>
      </div>

      {selected === 'reject' && (
        <DropdownWrapper $highlight={highlightDropdown}>
          <DropdownLabel htmlFor={`reject_reason_${question.id}`}>
            Reason for rejection:
          </DropdownLabel>
          {rejectOptions.length > 0 ? (
            <SelectDropdown
              options={rejectOptions}
              value={rejectReason}
              onChange={value => setRejectReason(value)}
              placeholder="Select reason"
              className="min-w-[180px] bg-transparent"
              variant="transparent"
            />
          ) : (
            <p style={{ fontSize: '0.875rem', color: '#6B7280', fontStyle: 'italic' }}>
              No rejection reasons available
            </p>
          )}
        </DropdownWrapper>
      )}
    </SectionWrapper>
  );
}
